// 雲端 AI（plan/vms-web-v0.md §5.10 AI-01 / AI-04）：訂閱狀態與本月用量、同意書與同意紀錄、功能、逐台排除、撤回。
//   - 主體 = 組織或 personal home，跟頁首範圍（SPEC #vms-web-835、AI-01；頁內不另放主體下拉，VMS UX 評審 2026-10-13 P0-3）；
//     對不到主體時列出可選的主體，點了 = 切頁首範圍；同意只有 org_admin / home owner 能給（伺服器端 + 5 分鐘內重新驗證，SDK 自動跳重新驗證）
//   - 沒有訂閱 → 導到方案與帳單；額度 80% / 100% 顯示橫幅
import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { KinioError } from '@kinio/web-sdk';
import { useScope } from '../../lib/scope';
import { useDevices } from '../../lib/useDevices';
import { fullName } from '../../lib/devices';
import { aiCloudApi, isOrgAdmin, type BillingSubjectRef } from '../../lib/planeApi';
import { AnalyzerTable } from './AnalyzerTable';
import { subjectKeyOf } from '../billing/BillingPage';
import { stamp } from '../../lib/timefmt';

export interface Subj extends BillingSubjectRef {
  name: string;
  canConsent: boolean;
}

const FEATURES = ['detect', 'attributes', 'caption'] as const;

/** AI 的主體（組織或 personal home）與頁首範圍對到的那一個；雲端 AI 與自帶 AI 共用。 */
export function useAiSubjects() {
  const { orgs, homes, org, scope } = useScope();
  const subjects = useMemo<Subj[]>(
    () => [
      ...orgs.filter((o) => !o.notice).map((o) => ({ api: o.api, kind: 'org' as const, id: o.org_id, name: o.display_name, canConsent: isOrgAdmin(o) })),
      ...homes
        .filter((h) => h.org_kind === 'personal' && !!h.role)
        .map((h) => ({ api: h.endpoints.api, kind: 'home' as const, id: h.home_id, name: h.display_name || h.home_id, canConsent: h.role === 'owner' })),
    ],
    [orgs, homes],
  );
  const key = subjectKeyOf(scope, org?.org_id, subjects);
  return { subjects, key, subject: subjects.find((s) => `${s.kind}:${s.id}` === key) };
}

export function CloudAiSection() {
  const { t } = useTranslation();
  const { setScope } = useScope();
  const { subjects, key, subject } = useAiSubjects();
  const label = (s: Subj) => (s.kind === 'org' ? t('billing.subject_org', { name: s.name }) : t('billing.subject_home', { name: s.name }));
  if (!subjects.length) return <p className="muted">{t('aiCloud.no_subject')}</p>;
  return (
    <section className="card stack" data-testid="cloud-ai">
      <h2 className="section-title">{t('aiCloud.title')}</h2>
      {subject ? (
        <p className="muted small" data-testid="cloud-ai-subject">
          {t('billing.capsule', { name: subject.name })}
        </p>
      ) : (
        <div data-testid="cloud-ai-chooser">
          <p className="muted small">{t('aiCloud.pick_subject')}</p>
          <div className="row">
            {subjects.map((s) => (
              <button key={`${s.kind}:${s.id}`} className="btn btn-sm" onClick={() => setScope(`${s.kind}:${s.id}`, '', { announce: t('scope.switched_to', { name: s.name }) })}>
                {label(s)}
              </button>
            ))}
          </div>
        </div>
      )}
      {subject && <SubjectCloudAi key={key} s={subject} />}
    </section>
  );
}

function errText(t: (k: string, o?: Record<string, unknown>) => string, e: unknown): string {
  if (e instanceof KinioError) return t(`aiCloud.err_${e.code}`, { defaultValue: t('billing.err_generic', { code: e.code }) });
  return String(e);
}

function SubjectCloudAi({ s }: { s: Subj }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { cameras, tree } = useDevices();
  const { homes: scopeHomes } = useScope();
  const usage = useQuery({ queryKey: ['ai-usage', s.kind, s.id], queryFn: () => aiCloudApi.usage(s), refetchInterval: 15_000 });
  const consent = useQuery({ queryKey: ['ai-consent', s.kind, s.id], queryFn: () => aiCloudApi.consent(s) });
  const [feats, setFeats] = useState<string[] | null>(null);
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['ai-consent', s.kind, s.id] });
    void qc.invalidateQueries({ queryKey: ['ai-usage', s.kind, s.id] });
  };
  const run = async (f: () => Promise<unknown>) => {
    setBusy(true);
    setErr(null);
    try {
      await f();
      refresh();
    } catch (e) {
      setErr(errText(t, e));
    }
    setBusy(false);
  };
  const ai = usage.data;
  const c = consent.data?.consent ?? null;
  const textVersion = consent.data?.text_version ?? '';
  const outdated = !!c && c.text_version !== textVersion;
  const chosen = feats ?? c?.features ?? ['detect', 'caption'];
  const entitled = ai?.features ?? [];
  // 主體底下的攝影機（org：org 的家戶；家戶：自己）
  const cams = cameras.filter((x) => (s.kind === 'home' ? x.homeId === s.id : true));
  const pct = ai && ai.limit > 0 ? Math.min(100, Math.round((ai.used * 100) / ai.limit)) : 0;

  if (usage.isLoading || consent.isLoading) return <p className="muted">{t('common.loading')}</p>;
  return (
    <>
      {!ai?.active ? (
        <p className="muted">
          {t('aiCloud.no_subscription')} <Link to="/billing">{t('aiCloud.go_billing')}</Link>
        </p>
      ) : (
        <div>
          <div>{t('billing.ai_usage', { used: ai.used, limit: ai.limit, period: ai.period })}</div>
          <div className="meter" role="meter" aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct}>
            <div className={`meter-fill ${pct >= 100 ? 'err' : pct >= 80 ? 'warn' : ''}`} style={{ width: `${pct}%` }} />
          </div>
          {pct >= 100 && <p className="error small">{t('billing.ai_limit_reached')}</p>}
          {pct >= 80 && pct < 100 && <p className="small">{t('billing.ai_near_limit')}</p>}
        </div>
      )}
      {err && <p className="error">{err}</p>}
      {c && !outdated ? (
        <div className="stack" data-testid="ai-consent-active">
          <p>
            <span className="badge badge-ok">{t('aiCloud.consented')}</span>{' '}
            {t('aiCloud.consented_at', { at: stamp(c.granted_at) })} · {c.features.map((f) => t(`billing.ai_feature_${f}`)).join(' · ')}
          </p>
          <div>
            <div className="small muted">{t('aiCloud.excluded_help')}</div>
            <div className="toolbar wrap">
              {cams.map((cam) => {
                const on = c.excluded_refs.includes(cam.aid);
                return (
                  <label key={cam.key} className={`chip ${on ? 'on' : ''}`}>
                    <input
                      type="checkbox"
                      checked={on}
                      disabled={!s.canConsent || busy}
                      onChange={() =>
                        void run(() =>
                          aiCloudApi.exclusions(s, {
                            excluded_refs: on ? c.excluded_refs.filter((x) => x !== cam.aid) : [...c.excluded_refs, cam.aid],
                            expected_version: c.version,
                          }),
                        )
                      }
                    />{' '}
                    {t('aiCloud.exclude')} {fullName(cam, tree)}
                  </label>
                );
              })}
            </div>
          </div>
          {s.canConsent && (
            <div>
              <button className="btn" disabled={busy} onClick={() => void run(() => aiCloudApi.revoke(s))}>
                {t('aiCloud.revoke')}
              </button>{' '}
              <span className="muted small">{t('aiCloud.revoke_help')}</span>
            </div>
          )}
        </div>
      ) : (
        <div className="stack" data-testid="ai-consent-form">
          {outdated && <p className="badge badge-warn">{t('aiCloud.outdated')}</p>}
          {/* 同意書收成一段摘要＋〔看完整說明〕，不整段放第一屏（SPEC AI-01，P1-11） */}
          <p>{t('aiCloud.consent_summary')}</p>
          <details className="card" data-testid="ai-consent-full">
            <summary>{t('aiCloud.consent_more')}</summary>
            <div style={{ whiteSpace: 'pre-line' }}>{t('aiCloud.consent_body')}</div>
          </details>
          <div className="toolbar wrap">
            {FEATURES.map((f) => (
              <label key={f} className="chip">
                <input
                  type="checkbox"
                  checked={chosen.includes(f)}
                  disabled={!entitled.includes(f) && entitled.length > 0}
                  onChange={() => setFeats(chosen.includes(f) ? chosen.filter((x) => x !== f) : [...chosen, f])}
                />{' '}
                {t(`billing.ai_feature_${f}`)}
              </label>
            ))}
          </div>
          {s.canConsent ? (
            <>
              <label>
                <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} /> {t('aiCloud.agree')}
              </label>
              <div>
                <button
                  className="btn btn-primary"
                  disabled={!agree || busy || chosen.length === 0}
                  onClick={() => void run(() => aiCloudApi.grant(s, { text_version: textVersion, features: chosen, excluded_refs: c?.excluded_refs ?? [] }))}
                >
                  {t('aiCloud.grant')}
                </button>{' '}
                <span className="muted small">{t('aiCloud.grant_help')}</span>
              </div>
            </>
          ) : (
            <p className="muted">{t(s.kind === 'org' ? 'aiCloud.need_org_admin' : 'aiCloud.need_owner')}</p>
          )}
        </div>
      )}
      <AnalyzerTable
        cams={cams}
        tree={tree}
        homes={[...new Set(cams.map((x) => x.homeId))].flatMap((id) => {
          const h = scopeHomes.find((x) => x.home_id === id);
          return h && !h.external ? [{ api: h.endpoints.api, homeId: id }] : [];
        })}
        consent={c}
        consentCurrent={!outdated}
        ai={ai}
        cloudError={!!usage.error || !!consent.error}
        canToggle={s.canConsent}
        busy={busy}
        onToggle={(on) => c && void run(() => aiCloudApi.localToo(s, { analyze_local_too: on, expected_version: c.version }))}
      />
    </>
  );
}
